import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CollegePortalJobsService } from '../../../college-workspace/services/college-portal-jobs.service';
import { CollegeJobListItem, CollegeJobListParams, CollegeJobStats } from '../../../college-workspace/models/college-jobs.models';
import { CollegeDashboardService } from '../../services/college-dashboard.service';

@Component({
  selector: 'app-college-jobs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './college-jobs.component.html',
  styleUrl: './college-jobs.component.scss'
})
export class CollegeJobsComponent implements OnInit, OnDestroy {
  @Output() selectJob = new EventEmitter<string>();

  query: string = '';
  locationQuery: string = '';
  deadlineFilter: string = 'All Deadlines';
  statusFilter: string = 'All statuses';

  // Course filtering now comes from the shared header's Department selector
  // rather than a second, duplicate dropdown here — see loadJobs().
  private course?: string;

  deadlineOptions: string[] = ['All Deadlines', 'This week', 'Next week', 'This month', 'Overdue'];
  statusOptions: string[] = ['All statuses', 'Applications Open', 'Assessment Ongoing', 'Interviews Ongoing', 'Applications Closed'];

  stats: CollegeJobStats | null = null;
  jobs: CollegeJobListItem[] = [];
  total = 0;
  page = 1;
  limit = 5;
  totalPages = 1;
  isLoading = false;

  private searchSubject = new Subject<void>();
  private subscription = new Subscription();

  constructor(
    private jobsService: CollegePortalJobsService,
    private dashboardService: CollegeDashboardService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(400)).subscribe(() => {
        this.page = 1;
        this.loadJobs();
      })
    );
    this.subscription.add(
      this.dashboardService.settings$.subscribe((settings) => {
        this.course = settings.department !== 'All Departments' ? settings.department : undefined;
        this.page = 1;
        this.loadJobs();
        this.loadStats();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get pageNumbers(): number[] {
    const windowSize = 5;
    let start = Math.max(1, this.page - Math.floor(windowSize / 2));
    const end = Math.min(this.totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  onSearchInput(): void {
    this.searchSubject.next();
  }

  onDeadlineChange(value: string): void {
    this.deadlineFilter = value;
    this.page = 1;
    this.loadJobs();
  }

  onStatusChange(value: string): void {
    this.statusFilter = value;
    this.page = 1;
    this.loadJobs();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.loadJobs();
  }

  onJobClick(job: CollegeJobListItem): void {
    this.selectJob.emit(job.job_id);
  }

  loadJobs(): void {
    this.isLoading = true;
    const params: CollegeJobListParams = {
      search: this.query.trim() || undefined,
      course: this.course,
      location: this.locationQuery.trim() || undefined,
      deadline: this.mapDeadlineFilter(this.deadlineFilter),
      status: this.statusFilter !== 'All statuses' ? this.statusFilter : undefined,
      page: this.page,
      limit: this.limit
    };

    this.jobsService.getJobs(params).pipe(
      finalize(() => { this.isLoading = false; })
    ).subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          this.jobs = res.Data.items;
          this.total = res.Data.total;
          this.totalPages = res.Data.total_pages;
        } else {
          this.toastr.error(res.Message || 'Failed to load job opportunities.');
        }
      },
      error: () => {
        this.toastr.error('Failed to load job opportunities.');
      }
    });
  }

  private loadStats(): void {
    this.jobsService.getStats({ course: this.course }).subscribe({
      next: (res) => {
        if (res.Success) this.stats = res.Data;
      },
      error: () => {
        // Leave stats blank rather than blocking the rest of the page.
      }
    });
  }

  private mapDeadlineFilter(value: string): string | undefined {
    switch (value) {
      case 'This week': return 'this_week';
      case 'Next week': return 'next_week';
      case 'This month': return 'this_month';
      case 'Overdue': return 'overdue';
      default: return undefined;
    }
  }
}
